/**
 * Shared Nomad layout / component class maps for GestionTransferts.
 * Replaces former CSS modules — use these instead of styles.module.css.
 */

import { inputClass } from './tw/twClasses'

const inputish = inputClass

export const transferPageClass =
  'w-full px-3 py-6 sm:px-4'

export const transferCardClass =
  'mb-4 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] p-4 shadow-[var(--shadow-sm)] sm:p-5'

export const transferActionsClass = 'flex items-center justify-end gap-1'

export const transferCellPrimaryClass = 'text-sm font-medium text-[var(--foreground)]'
export const transferCellMutedClass = 'text-xs text-[var(--muted-foreground)]'

export const transferBtnPrimaryClass =
  'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-[var(--radius-md)] border border-transparent bg-[var(--gradient-brand)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] shadow-[var(--shadow-glow)] transition-opacity hover:opacity-92 disabled:cursor-not-allowed disabled:opacity-50'

export const transferBtnSecondaryClass =
  'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] px-4 py-2 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-50'

export const transferBtnDangerClass =
  'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-[var(--radius-md)] border border-[color-mix(in_oklab,var(--destructive)_35%,var(--border))] bg-[var(--card)] px-4 py-2 text-sm font-medium text-[var(--destructive)] transition-colors hover:bg-[color-mix(in_oklab,var(--destructive)_10%,var(--card))] disabled:cursor-not-allowed disabled:opacity-50'

export const transferIconBtnClass =
  'inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--muted-foreground)] transition-colors hover:bg-[var(--accent)] hover:text-[var(--primary)]'

export const transferModalFooterClass = 'mt-4 flex flex-wrap items-center justify-end gap-2'

/** Generic page-level style bag used by many transfer screens */
export const transferStyles = {
  page: transferPageClass,
  actions: transferActionsClass,
  cellPrimary: transferCellPrimaryClass,
  cellMuted: transferCellMutedClass,
  btnPrimary: transferBtnPrimaryClass,
  btnSecondary: transferBtnSecondaryClass,
  btnDanger: transferBtnDangerClass,
  iconBtn: transferIconBtnClass,
  modalFooter: transferModalFooterClass,
  tarifSection: 'mt-4 space-y-4',
  vehicleCell: 'flex items-center gap-3',
  thumb: 'h-12 w-12 rounded-[var(--radius-sm)] border border-[var(--border)] object-cover',
  name: 'text-sm font-semibold text-[var(--foreground)]',
  category: 'mt-0.5 text-xs text-[var(--muted-foreground)]',
  luggageRow: 'flex flex-wrap gap-1.5',
  mutedText: 'text-[13px] italic text-[var(--muted-foreground)]',
  deleteText: 'text-sm text-[var(--foreground)]',
  refCell: 'font-medium text-[var(--primary)] hover:underline',
  tripCell: 'flex items-center gap-2 text-sm',
  tripIcon: 'inline-flex items-center text-[var(--muted-foreground)]',
  arrow: 'text-[var(--muted-foreground)]',
  amountCell: 'text-right',
  amountValue: 'ui-numeric text-sm font-semibold text-[var(--foreground)]',
  titleInline: 'text-base font-semibold text-[var(--foreground)]',
  grid: 'grid gap-4 lg:grid-cols-[1.4fr_1fr]',
  left: 'min-w-0 space-y-4',
  right: 'min-w-0 space-y-4',
  clientRow: 'flex items-start gap-3',
  avatar: 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--muted)] text-sm font-semibold text-[var(--foreground)]',
  clientName: 'text-sm font-semibold text-[var(--foreground)]',
  infoGrid: 'grid gap-3 sm:grid-cols-2',
  infoLabel: 'text-xs text-[var(--muted-foreground)]',
  infoValue: 'text-sm font-medium text-[var(--foreground)]',
  totalBox: 'rounded-[var(--radius-md)] border border-[var(--border)] bg-[color-mix(in_oklab,var(--muted)_40%,var(--card))] p-4',
  totalLabel: 'text-xs text-[var(--muted-foreground)]',
  totalAmount: 'ui-numeric mt-1 text-xl font-semibold text-[var(--foreground)]',
  totalRow: 'mt-2 flex items-center justify-between gap-2',
  divider: 'my-3 border-t border-[var(--border)]',
  section: 'space-y-3',
  sectionHeading: 'text-sm font-semibold text-[var(--foreground)]',
  sectionDesc: 'text-xs text-[var(--muted-foreground)]',
  sectionLabel: 'mb-1.5 block text-xs font-medium text-[var(--muted-foreground)]',
  form: 'space-y-4',
  formFooter: 'mt-4 flex flex-wrap justify-end gap-2',
  footer: 'mt-4 flex flex-wrap justify-end gap-2',
  fieldGridResponsive: 'grid gap-3 sm:grid-cols-2',
  fieldHint: 'mt-1 text-xs text-[var(--muted-foreground)]',
  grid2: 'grid gap-3 sm:grid-cols-2',
  twoPanel: 'grid gap-4 lg:grid-cols-2',
  tableScroll: 'overflow-x-auto',
  table: 'min-w-full text-sm',
  tableHead: 'bg-[color-mix(in_oklab,var(--muted)_55%,var(--card))]',
  tableHeader: 'h-10 px-2 text-left text-sm font-medium text-[var(--muted-foreground)]',
  tableHeaderText: 'text-sm font-medium text-[var(--muted-foreground)]',
  tableRow: 'border-b border-[var(--border)]',
  tableRowStack: 'space-y-1',
  tableBlock: 'overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)]',
  emptyRow: 'px-2 py-8 text-center text-sm text-[var(--muted-foreground)]',
  muted: 'text-[var(--muted-foreground)]',
  lead: 'text-sm text-[var(--muted-foreground)]',
  subTitle: 'text-sm font-medium text-[var(--foreground)]',
  subTitleHint: 'text-xs text-[var(--muted-foreground)]',
  badge: 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
  periodCell: 'flex items-center gap-2 text-sm',
  periodIcon: 'text-[var(--muted-foreground)]',
  positive: 'text-[var(--success)]',
  negative: 'text-[var(--destructive)]',
  red: 'text-[var(--destructive)]',
  inclus: 'text-xs text-[var(--muted-foreground)]',
  paymentStatus: 'text-xs font-medium',
  capacityRow: 'flex flex-wrap gap-2 text-xs text-[var(--muted-foreground)]',
  benefitRow: 'flex items-start gap-2 text-sm',
  extrasList: 'flex flex-wrap gap-1.5',
  extraItem: 'rounded-[var(--radius-sm)] bg-[var(--muted)] px-2 py-0.5 text-xs',
  vehicleList: 'grid grid-cols-1 gap-3 md:grid-cols-2',
  vehicleRow: 'flex gap-3 rounded-[var(--radius-md)] border border-[var(--border)] p-3',
  vehicleThumb: 'h-16 w-20 shrink-0 rounded-[var(--radius-sm)] object-cover',
  vehicleInfo: 'min-w-0 flex-1',
  vehicleName: 'text-sm font-semibold text-[var(--foreground)]',
  previewWrap: 'max-w-full overflow-x-auto rounded-[var(--radius-md)] border border-[var(--border)]',
  previewFrame: 'aspect-video w-full bg-[var(--muted)]',
  previewImg: 'h-full w-full object-cover',
  dropzone: 'flex cursor-pointer flex-col items-center justify-center rounded-[var(--radius-md)] border border-dashed border-[var(--border)] bg-[color-mix(in_oklab,var(--muted)_35%,var(--card))] px-4 py-8 text-center text-sm text-[var(--muted-foreground)] transition-colors hover:bg-[var(--accent)]',
  dropzoneDisabled: 'pointer-events-none opacity-50',
  photoColumn: 'space-y-3',
  layout: 'space-y-4',
  mainColumn: 'min-w-0 space-y-4',
  module: 'space-y-4',
  card: transferCardClass,
  header: 'mb-4 flex flex-wrap items-center justify-between gap-3',
  titleWrap: 'flex flex-wrap items-center gap-2',
  title: 'm-0 text-sm font-semibold text-[var(--foreground)]',
  icon: 'inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] bg-[color-mix(in_oklab,var(--primary)_12%,transparent)] text-[var(--primary)]',
  body: 'text-sm text-[var(--foreground)]',
  wrapper: 'overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-sm)]',
  bar: 'mb-4 flex flex-wrap items-center gap-3',
  searchField: 'min-w-[220px] flex-1',
  filters: 'flex flex-wrap items-center gap-3',
  btn: 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-md)] text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  // text emphasis (tables/lists) — not button variants
  primary: 'text-sm font-semibold text-[var(--foreground)]',
  secondary: 'text-xs text-[var(--muted-foreground)]',
  danger: 'text-[var(--destructive)]',
  green: 'text-[var(--success)]',
  ghost: 'inline-flex items-center gap-2 rounded-[var(--radius-md)] px-2.5 py-1.5 text-sm font-medium text-[var(--primary)] hover:bg-[color-mix(in_oklab,var(--primary)_10%,transparent)]',
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-3 text-sm',
  tag: 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium leading-none',
  neutral: 'bg-[var(--muted)] text-[var(--foreground)]',
  blue: 'bg-[color-mix(in_oklab,var(--primary)_12%,transparent)] text-[var(--primary)]',
  tagGreen: 'bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-[var(--success)]',
  tagRed: 'bg-[color-mix(in_oklab,var(--destructive)_12%,transparent)] text-[var(--destructive)]',
  amber: 'bg-[color-mix(in_oklab,var(--warning)_16%,transparent)] text-[var(--warning)]',
  confirmed: 'bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-[var(--success)]',
  pending: 'bg-[color-mix(in_oklab,var(--warning)_16%,transparent)] text-[var(--warning)]',
  cancelled: 'bg-[color-mix(in_oklab,var(--destructive)_12%,transparent)] text-[var(--destructive)]',
  done: 'bg-[var(--muted)] text-[var(--foreground)]',
  timeline: 'relative m-0 list-none p-0',
  step: 'relative flex gap-3.5 pb-6 last:pb-0',
  dot: 'z-[1] mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full',
  dotStart: 'bg-[var(--primary)] shadow-[0_0_0_4px_color-mix(in_oklab,var(--primary)_18%,transparent)]',
  dotEnd: 'bg-[var(--muted-foreground)] shadow-[0_0_0_4px_color-mix(in_oklab,var(--muted-foreground)_18%,transparent)]',
  connector: 'absolute bottom-1 left-[6px] top-6 w-0.5 bg-[repeating-linear-gradient(to_bottom,var(--border)_0_3px,transparent_3px_6px)]',
  content: 'min-w-0 flex-1',
  time: 'mb-1 text-[15px] font-semibold text-[var(--primary)]',
  extra: 'mt-1 text-xs text-[var(--muted-foreground)]',
  reductionValue: 'ui-numeric text-sm font-semibold text-[var(--success)]',
  sectionFooter: 'mt-3 text-sm',
  breadcrumb: 'flex flex-wrap items-center gap-2 text-[13px] text-[var(--muted-foreground)]',
  breadcrumbLink: 'font-normal text-[var(--muted-foreground)] no-underline hover:text-[var(--primary)]',
  breadcrumbActive: 'font-semibold text-[var(--foreground)]',
  breadcrumbSep: 'text-xs text-[var(--muted-foreground)]',
  titleBlock: 'min-w-[280px] flex-1',
  subtitle: 'mt-1.5 text-sm text-[var(--muted-foreground)]',
  countBadge: 'inline-flex items-center rounded-full bg-[var(--muted)] px-2.5 py-0.5 text-xs font-medium text-[var(--foreground)]',
  row: 'flex flex-wrap items-start justify-between gap-4',
  // ConfigurationForms / promo / global rules
  configurationGeneralLayout: 'space-y-4',
  generalPanel: transferCardClass,
  generalSection: 'space-y-3',
  configBlock: 'rounded-[var(--radius-md)] border border-[var(--border)] p-3',
  configBlockTitle: 'mb-2 text-sm font-semibold text-[var(--foreground)]',
  contractFieldsGrid: 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3',
  globalRulesCard: transferCardClass,
  globalRulesForm: 'space-y-4',
  globalRulesFields: 'grid gap-3 sm:grid-cols-2',
  globalRulesField: 'space-y-1.5',
  globalRulesControl: 'w-full',
  globalRulesControlNarrow: 'max-w-xs',
  globalRulesSelect: inputish,
  globalRulesFooter: 'mt-4 flex flex-wrap justify-end gap-2',
  globalRulesHelp: 'text-xs text-[var(--muted-foreground)]',
  helpText: 'text-xs text-[var(--muted-foreground)]',
  computed: 'ui-numeric text-sm font-medium text-[var(--foreground)]',
  fieldFull: 'col-span-full',
  grid5: 'grid gap-3 sm:grid-cols-2 lg:grid-cols-5',
  groupPanel: 'overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)]',
  groupCollapseToggle: 'flex w-full items-center justify-between gap-3 bg-[color-mix(in_oklab,var(--muted)_40%,var(--card))] px-3 py-2 text-left',
  groupCollapseLeft: 'flex min-w-0 items-center gap-2',
  groupCollapseTitle: 'truncate text-sm font-semibold text-[var(--foreground)]',
  groupCollapsedMeta: 'text-xs text-[var(--muted-foreground)]',
  groupChevron: 'text-[var(--muted-foreground)]',
  groupBadge: 'rounded-full bg-[var(--muted)] px-2 py-0.5 text-[11px] font-medium',
  groupBulkApply: 'flex flex-wrap items-end gap-2 border-b border-[var(--border)] p-3',
  groupBulkField: 'min-w-[8rem]',
  groupBulkMargin: 'space-y-2 p-3',
  groupBulkMarginHead: 'flex flex-wrap items-center justify-between gap-2',
  groupBulkMarginLabel: 'text-xs font-medium text-[var(--muted-foreground)]',
  groupBulkMarginHint: 'text-xs text-[var(--muted-foreground)]',
  groupBulkMarginRow: 'grid gap-2 sm:grid-cols-[1fr_auto]',
  promoHeaderRow: 'mb-3 flex flex-wrap items-center justify-between gap-2',
  promoSupplierGroup: 'mb-4 space-y-2',
  promoSupplierTitle: 'text-sm font-semibold text-[var(--foreground)]',
  promoGlobalSetter: 'rounded-[var(--radius-md)] border border-[var(--border)] p-3',
  promoGlobalSetterRow: 'flex flex-wrap items-end gap-2',
  promoGlobalValueInput: 'ui-numeric max-w-[8rem]',
  promoApplyAllBtn: transferBtnSecondaryClass,
  promoTargetsTableWrap: 'overflow-x-auto rounded-[var(--radius-md)] border border-[var(--border)]',
  promoTargetsTable: 'min-w-full text-sm',
  promoTargetsCheckCol: 'w-10 px-2',
  promoTargetDetail: 'text-xs text-[var(--muted-foreground)]',
  saleField: 'space-y-1.5',
  villeGroupBlock: 'mb-4 rounded-[var(--radius-md)] border border-[var(--border)] p-3',
  villeGroupToolbar: 'mb-3 flex flex-wrap items-end gap-2',
  villeGroupToolbarFields: 'flex flex-wrap gap-2',
  villeGroupActions: 'flex flex-wrap gap-2',
  addVehicleLineFooter: 'mt-3 flex justify-end',
  colHint: 'text-[11px] text-[var(--muted-foreground)]',
  deleteBtn: transferBtnDangerClass,
  // Regles / voucher / recherche extras
  iconAmber: 'text-[var(--warning)]',
  iconRed: 'text-[var(--destructive)]',
  loadingHint: 'text-center text-sm text-[var(--muted-foreground)]',
  calcRow: 'flex items-center justify-between gap-2 text-sm',
  supplier: 'text-xs text-[var(--muted-foreground)]',
  ruleTitle: 'text-sm font-medium text-[var(--foreground)]',
  ruleDesc: 'text-xs text-[var(--muted-foreground)]',
  ruleIcon: 'text-[var(--primary)]',
  rulesList: 'space-y-2',
  autoPaymentEmbed: 'rounded-[var(--radius-md)] border border-[var(--border)] p-3',
  // vehicle modal extras
  extrasHint: 'text-xs text-[var(--muted-foreground)]',
  fieldGrid2: 'grid gap-3 sm:grid-cols-2',
  hint: 'text-xs text-[var(--muted-foreground)]',
  topRowPair: 'grid gap-3 sm:grid-cols-2',
}
